import { keyframes, styled, useTheme } from "@mui/material/styles";

const ANIMATION_DURATION = "6s";

// Each satellite replicates the original SVGator transform chain translate(origin) rotate(angle) translate(offset).
// Outer-orbit satellites sweep 180deg -> -180deg, inner-orbit ones 180deg -> 540deg, all linear.
const outerTopOrbit = keyframes`
  from { transform: translate(120px, 124.999996px) rotate(180deg) translate(-0.192px, 117.807246px); }
  to { transform: translate(120px, 124.999996px) rotate(-180deg) translate(-0.192px, 117.807246px); }
`;

const outerBottomRightOrbit = keyframes`
  from { transform: translate(120px, 125.005112px) rotate(180deg) translate(-83.192px, -87.187888px); }
  to { transform: translate(120px, 125.005112px) rotate(-180deg) translate(-83.192px, -87.187888px); }
`;

const outerBottomLeftOrbit = keyframes`
  from { transform: translate(120.192px, 124.999996px) rotate(180deg) translate(102px, -67.193004px); }
  to { transform: translate(120.192px, 124.999996px) rotate(-180deg) translate(102px, -67.193004px); }
`;

const innerTopLeftOrbit = keyframes`
  from { transform: translate(120.192px, 125.005112px) rotate(180deg) translate(71px, 56.812812px); }
  to { transform: translate(120.192px, 125.005112px) rotate(540deg) translate(71px, 56.812812px); }
`;

const innerRightOrbit = keyframes`
  from { transform: translate(120.192px, 125.004132px) rotate(180deg) translate(-90px, -9.187888px); }
  to { transform: translate(120.192px, 125.004132px) rotate(540deg) translate(-90px, -9.187888px); }
`;

const innerBottomOrbit = keyframes`
  from { transform: translate(120.192px, 124.999996px) rotate(180deg) translate(3px, -90.193004px); }
  to { transform: translate(120.192px, 124.999996px) rotate(540deg) translate(3px, -90.193004px); }
`;

const dotBlink = keyframes`
  0% { opacity: 1; }
  50% { opacity: 1; animation-timing-function: ease-in; }
  53.3333% { opacity: 0; animation-timing-function: ease-in-out; }
  56.6667% { opacity: 1; }
  100% { opacity: 1; }
`;

const AnimatedSvg = styled("svg")({
  "& .satellite": {
    transformBox: "view-box",
    transformOrigin: "0 0",
  },
  "& .outerTopSatellite": {
    animation: `${outerTopOrbit} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .outerBottomRightSatellite": {
    animation: `${outerBottomRightOrbit} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .outerBottomLeftSatellite": {
    animation: `${outerBottomLeftOrbit} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .innerTopLeftSatellite": {
    animation: `${innerTopLeftOrbit} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .innerRightSatellite": {
    animation: `${innerRightOrbit} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .innerBottomSatellite": {
    animation: `${innerBottomOrbit} ${ANIMATION_DURATION} linear infinite`,
  },
  "& .blinkingDot": {
    animation: `${dotBlink} ${ANIMATION_DURATION} linear infinite`,
  },
});

const AuthLoaderSvg = () => {
  const { palette } = useTheme();

  return (
    // The original viewBox "-10 -10 260 267" is normalized to a zero origin (with the offset moved into a
    // wrapper group) because transform-origin resolution against a viewBox with a nonzero origin is
    // browser-dependent; with a zero origin, `transform-origin: 0 0` is exact everywhere.
    <AnimatedSvg
      id="eC5RTXPJKvM1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 260 267"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <g transform="translate(10 10)">
        <circle
          id="eC5RTXPJKvM2"
          r="60"
          transform="matrix(1 0 0 1 120 125)"
          fill={palette.primary.main}
          stroke="none"
          strokeWidth="1"
        />
        <path
          id="eC5RTXPJKvM3"
          d="M151.677,124.864L151.359,124.163C145.206,110.672,131.23,101.208,117.248,101C116.994,100.996,116.785,101.204,116.785,101.457L116.785,104.597C116.785,104.845,116.985,105.04,117.23,105.045C122.075,105.121,126.92,106.456,131.357,108.772C127.511,113.305,125.585,119.657,126.166,126.163C126.675,131.831,129.027,136.876,132.751,140.468C127.946,143.25,122.588,144.869,117.235,144.955C116.985,144.96,116.79,145.159,116.79,145.403L116.79,148.543C116.79,148.796,116.998,149.004,117.253,149C131.234,148.796,145.211,139.328,151.364,125.837L151.682,125.136C151.718,125.05,151.718,124.95,151.677,124.864ZM147.195,125.149C144.63,130.325,140.788,134.79,136.243,138.165C132.832,135.233,130.662,130.827,130.212,125.801C129.704,120.114,131.448,114.608,134.894,110.881C140.03,114.346,144.38,119.173,147.195,124.851C147.241,124.946,147.241,125.059,147.195,125.149Z"
          fill={palette.primary.contrastText}
          stroke="none"
          strokeWidth="1"
        />
        <path
          id="eC5RTXPJKvM4"
          d="M108.543,116.685C108.466,116.821,108.512,116.998,108.648,117.074L111.527,118.717C111.745,118.839,112.022,118.766,112.144,118.549L116.721,110.596C116.876,110.325,116.717,109.985,116.413,109.927L89.5398,104.832C89.1811,104.765,88.8905,105.127,89.0403,105.461L97.7088,124.729C97.7633,124.847,97.7633,124.978,97.7088,125.096L89.0539,144.549C88.9041,144.884,89.1947,145.246,89.5534,145.178L116.413,140.084C116.717,140.025,116.876,139.686,116.721,139.414L112.149,131.461C112.026,131.244,111.745,131.167,111.531,131.293L108.602,132.967C108.493,133.031,108.457,133.166,108.516,133.275L110.487,136.704C110.587,136.876,110.487,137.089,110.292,137.129L96.0923,139.821C95.8652,139.867,95.6836,139.636,95.7744,139.423L101.986,125.458C102.141,125.105,102.141,124.702,101.986,124.354L95.7971,110.596C95.7018,110.383,95.8834,110.153,96.115,110.198L110.292,112.885C110.487,112.921,110.587,113.139,110.487,113.311L108.543,116.685Z"
          fill={palette.primary.contrastText}
          stroke="none"
          strokeWidth="1"
        />
        <path
          id="eC5RTXPJKvM5"
          d="M135.058,121.56C136.369,121.56,137.432,120.501,137.432,119.194C137.432,117.887,136.369,116.828,135.058,116.828C133.746,116.828,132.683,117.887,132.683,119.194C132.683,120.501,133.746,121.56,135.058,121.56Z"
          transform="matrix(1 0 0 0.181773 0 97.527749)"
          fill={palette.primary.contrastText}
          stroke="none"
          strokeWidth="1"
        />
        <path
          id="eC5RTXPJKvM6"
          className="blinkingDot"
          d="M135.058,121.56C136.369,121.56,137.432,120.501,137.432,119.194C137.432,117.887,136.369,116.828,135.058,116.828C133.746,116.828,132.683,117.887,132.683,119.194C132.683,120.501,133.746,121.56,135.058,121.56Z"
          fill={palette.primary.contrastText}
          stroke="none"
          strokeWidth="1"
        />
        <circle
          id="eC5RTXPJKvM7"
          r="119.5"
          transform="matrix(1 0 0 1 120 127)"
          fill="none"
          stroke={palette.divider}
          strokeWidth="1"
          strokeDasharray="2,2"
        />
        <circle
          id="eC5RTXPJKvM8"
          className="satellite outerTopSatellite"
          r="6.69202"
          transform="matrix(-1 0 0 -1 120.192 7.19275)"
          fill={palette.background.grey}
          stroke={palette.primary.main}
          strokeWidth="1"
        />
        <circle
          id="eC5RTXPJKvM9"
          className="satellite outerBottomRightSatellite"
          r="7.19202"
          transform="matrix(-1 0 0 -1 203.192 212.193)"
          fill={palette.primary.main}
          stroke="none"
          strokeWidth="1"
        />
        <circle
          id="eC5RTXPJKvM10"
          className="satellite outerBottomLeftSatellite"
          r="6.69202"
          transform="matrix(-1 0 0 -1 18.192 192.193)"
          fill={palette.background.grey}
          stroke={palette.primary.main}
          strokeWidth="1"
        />
        <circle
          id="eC5RTXPJKvM11"
          r="89.5"
          transform="matrix(1 0 0 1 120 125)"
          fill="none"
          stroke={palette.divider}
          strokeWidth="1"
          strokeDasharray="2,2"
        />
        <circle
          id="eC5RTXPJKvM12"
          className="satellite innerTopLeftSatellite"
          r="7.19202"
          transform="matrix(-1 0 0 -1 49.192 68.1923)"
          fill={palette.divider}
          stroke="none"
          strokeWidth="1"
        />
        <circle
          id="eC5RTXPJKvM13"
          className="satellite innerRightSatellite"
          r="7.19202"
          transform="matrix(-1 0 0 -1 210.192 134.19202)"
          fill={palette.divider}
          stroke="none"
          strokeWidth="1"
        />
        <circle
          id="eC5RTXPJKvM14"
          className="satellite innerBottomSatellite"
          r="6.69202"
          transform="matrix(-1 0 0 -1 117.192 215.193)"
          fill={palette.background.grey}
          stroke={palette.divider}
          strokeWidth="1"
        />
      </g>
    </AnimatedSvg>
  );
};

export default AuthLoaderSvg;
